import { getLogger } from '@jitsi/logger';

import { updateConfig } from '../../react/features/base/config/actions';
import {
    JitsiConnectionErrors
} from '../../react/features/base/lib-jitsi-meet';
import { updateSettings } from '../../react/features/base/settings/actions';
import { clearNotifications } from '../../react/features/notifications/actions';
import { isPrejoinEnabledInConfig } from '../../react/features/prejoin/functions.any';
import { setStorageData } from '../functions.any';
import PbxUserPresence from '../pbxUserPresence';
import WildixEvents from '../service/WildixEvents';


import Env from './Environment';


const logger = getLogger(__filename);


export default class PBXMethods {
    #conferenceToken = null;
    #authServiceToken = null;

    constructor() {
        this.pbxUserPresence = new PbxUserPresence();

        document.addEventListener(WildixEvents.CONFERENCE_JOINED, () => {
            this.#conferenceLoaded();
        });
    }

    /**
     *
     */
    #conferenceLoaded = () => {
        this.#initEvents();
        const { ctiextension } = APP.store.getState()['features/base/settings'];

        if (ctiextension || Env.isLocalHost()) {
            // Only for PBX users
            this.pbxUserPresence.setInConference();
        }
    };

    #initEvents = () => {
        APP.wildix.eventEmitter.on(WildixEvents.CONFERENCE_DISPOSE, this.exitFromConference.bind(this));
    };

    /**
     *
     * @param sync
     */
    exitFromConference = () => {
        const { ctiextension } = APP.store.getState()['features/base/settings'];

        if (ctiextension || Env.isLocalHost()) {
            // Only for PBX users
            this.pbxUserPresence.revert();
        }
    };

    /**
     *
     * @returns {Promise<any> | Promise}
     */
    getConferenceToken = token => new Promise((resolve, reject) => {
        const headers = APP.wildixApiMethods.addAuthHeader(token);

        APP.WildixAPI.ConferenceToken.get({}, headers).then(response => {
            if (!response.result && !response.result.token) {
                return reject();
            }
            this.#conferenceToken = response.result.token;
            resolve(this.#conferenceToken);
        })
        .catch(e => {
            logger.error('No ConferenceToken API', e);
            reject(e);
        });
    });

    getAuthServiceToken = token => new Promise((resolve, reject) => {
        const headers = APP.wildixApiMethods.addAuthHeader(token);

        APP.WildixAPI.AuthServiceToken.get({}, headers).then(response => {
            if (!response.result && !response.result.accessToken) {
                return reject();
            }
            this.#authServiceToken = response.result.accessToken;
            resolve(this.#authServiceToken);
        })
            .catch(e => {
                logger.error('No AuthServiceToken API', e);
                reject(e);
            });
    });

    userAuthWithCredentials = () => new Promise((resolve, reject) => {
        const fn = () => {
            this.getCredentials()
            .then(res => {
                resolve(res);
            })
            .catch(error => {
                if (error && error.status === 409) {
                    const c = setTimeout(() => {
                        fn();
                        clearTimeout(c);
                    }, 10000);
                } else if (error && error.status === 401) {
                    reject(JitsiConnectionErrors.PASSWORD_REQUIRED);
                } else {
                    reject(error.statusText);
                }
            });
        };

        fn();
    });

    /**
     *
     * @returns {*}
     */
    getCredentials = headers => {
        const { displayName, email } = APP.store.getState()['features/base/settings'];
        const { roomToken, pbxExtension, pbxSerial } = APP.store.getState()['features/base/config'];
        const extensionAndSerial = {
            extension: Env.isLocalHost() ? '777' : pbxExtension,
            serial: Env.isLocalHost() ? '2211000018ae' : pbxSerial
        };

        return APP.WildixAPI.Credentials
        .get({
            room: roomToken,
            name: displayName,
            email,
            ...extensionAndSerial
        }, headers)
        .then(this.#getCredentialsSuccess)
        .catch(this.#getCredentialsError);
    };

    /**
     *
     * @param data
     * @returns {*}
     */
    #getCredentialsSuccess = data => {
        const { pbxUser } = APP.store.getState()['features/base/config'];
        const { login: websocketUser, password: websocketPassword, subject, userRegion } = data.result;

        const deploymentInfoData = {
            region: userRegion,
            userRegion
        };

        config.websocketUser = websocketUser;
        config.websocketPassword = websocketPassword;
        config.deploymentInfo = deploymentInfoData;

        APP.store.dispatch(updateConfig({
            websocketUser,
            websocketPassword,
            callDisplayName: subject,
            deploymentInfo: deploymentInfoData
        }));

        if (pbxUser) {
            this.getUserInfo()
            .catch(e => {
                console.error('### PBX reaching problem: ', e);

                // Update user role in case of PBX not available
                APP.store.dispatch(updateSettings({
                    role: APP.wildix.methods.conferenceRole()
                }));
            });
        } else {
            APP.store.dispatch(updateSettings({
                role: APP.wildix.methods.conferenceRole()
            }));
        }

        APP.store.dispatch(updateSettings({
            region: userRegion
        }));

        APP.wildix.storage.setTitle(subject);
        APP.wildix.eventEmitter.emit(WildixEvents.HIDE_DIALOG, 'PopupNotification');

        return data;
    };

    /**
     *
     * @param error
     */

    #getCredentialsError = error => {
        if (error && error.status === 409) {
            APP.wildix.eventEmitter.emit(WildixEvents.OPEN_DIALOG,
                'PopupNotification', { title: 'notification.WaitingForAnOrganizer' });
        } else if (error && error.status === 401 && !this.#conferenceToken) {
            this.getTokenAndInitApp();
        } else {
            APP.wildix.eventEmitter.emit(WildixEvents.OPEN_DIALOG,
                'PopupNotification', { title: 'notification.ConferenceIsNotAvailableText' });
        }
        APP.wildix.storage.setTitle('');
        throw error;
    };

    getTokenAndInitApp = () => {
        const { proxied } = APP.store.getState()['features/base/config'];

        if (!proxied) {
            logger.error('getTokenAndInitApp allowed only for PBX users.');

            return false;
        }

        this.getPersonalToken().then(token => {
            this.getAuthServiceToken(token).then(authServiceToken => {
                this.getConferenceToken(authServiceToken).then(conferenceToken => {
                    this.initConferenceWithToken(conferenceToken);
                })
                .catch(personalAndConferenceTokenError => {
                    logger.error(personalAndConferenceTokenError);
                });
            })
            .catch(getAuthServiceTokenError => {
                logger.error(getAuthServiceTokenError);
            });
        })
        .catch(personalTokenError => {
            logger.error(personalTokenError);
        });
    };

    initConferenceWithToken = token => {
        APP.store.dispatch(clearNotifications());
        const headers = APP.wildixApiMethods.addAuthHeader(token);

        this.getCredentials(headers).then(() => {
            APP.conference.init({
                roomName: APP.wildix.storage.getRoomToken(),
                shouldDispatchConnect: !isPrejoinEnabledInConfig(APP.store.getState())
            });
        });
    };

    getPersonalToken = () => new Promise((resolve, reject) => {
        APP.WildixAPI.PersonalToken.get({ service: 'auth.wildix.com' }).then(response => {
            if (!response.result && !response.result.token) {
                return reject();
            }
            resolve(response.result.token);
        })
        .catch(e => {
            logger.error('No PersonalToken API', e);
            reject(e);
        });
    });

    getAuthToken = () => new Promise((resolve, reject) => {
        APP.WildixAPI.AuthToken.get({ service: 'auth.wildix.com' }).then(response => {
            if (!response.result && !response.result.token) {
                return reject();
            }
            resolve(response.result.token);
        })
        .catch(e => {
            logger.error('No AuthToken API', e);
            reject(e);
        });
    });

    objectLangCode = {
        'en-us': 'en',
        'es-us': 'esUS',
        'ee': 'et',
        'se': 'sv',
        'no': 'en'
    };

    setNameLangFromCollaboration(language) {
        const langLoverString = language.toLowerCase();
        let resultLan = language;

        if (this.objectLangCode[langLoverString]) {
            resultLan = this.objectLangCode[langLoverString];
        }

        setStorageData('collaborationLanguage', langLoverString);
        setStorageData('language', resultLan);
    }

    /**
     *
     * @returns {*}
     */
    getUserInfo = () => APP.WildixAPI.UserInfo.get()
    .then(response => {

        const { result } = response;
        const { pbxSerial, pbxHost } = APP.store.getState()['features/base/config'];

        const userData = {
            displayName: result.name,
            email: result.email,
            ctijid: `${result.extension}@${pbxSerial}`,
            ctiextension: result.extension,
            role: APP.wildix.methods.conferenceRole(),
            avatarURL: `${pbxHost}/user_avatar/${result.extension}.png`
        };

        // Update setting in store
        APP.store.dispatch(updateSettings(userData));

        this.setNameLangFromCollaboration(result.language);
    })
    .catch(e => {
        throw e;
    });

}
